import { useSyncExternalStore } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  LineChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

function getIsDark(): boolean {
  return document.documentElement.classList.contains('dark');
}

function subscribeDarkMode(callback: () => void): () => void {
  const observer = new MutationObserver(callback);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  return () => observer.disconnect();
}

function useDarkMode(): boolean {
  return useSyncExternalStore(subscribeDarkMode, getIsDark);
}

interface SeriesConfig {
  key: string;
  color: string;
  name: string;
  stackId?: string;
}

interface TimeSeriesChartProps {
  data: Record<string, unknown>[];
  series: SeriesConfig[];
  type?: 'bar' | 'line';
  xKey?: string;
  yFormatter?: (value: number) => string;
  tooltipFormatter?: (value: number) => string;
  syncId?: string;
}

function formatDateLabel(value: string): string {
  const d = new Date(value + 'T00:00:00');
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function formatDateFull(label: any): string {
  const s = String(label);
  const d = new Date(s + 'T00:00:00');
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function TimeSeriesChart({
  data,
  series,
  type = 'bar',
  xKey = 'bucket',
  yFormatter,
  tooltipFormatter,
  syncId,
}: TimeSeriesChartProps) {
  const dark = useDarkMode();
  const axisColor = dark ? '#9ca3af' : '#6b7280';
  const gridColor = dark ? '#374151' : '#e5e7eb';
  const tooltipBg = dark ? '#1f2937' : '#ffffff';
  const tooltipBorder = dark ? '#374151' : '#e5e7eb';

  const Chart = type === 'line' ? LineChart : BarChart;

  return (
    <ResponsiveContainer width="100%" height={240}>
      <Chart data={data} syncId={syncId} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
        <XAxis
          dataKey={xKey}
          tick={{ fill: axisColor, fontSize: 12 }}
          tickLine={false}
          axisLine={{ stroke: gridColor }}
          tickFormatter={formatDateLabel}
        />
        <YAxis
          tick={{ fill: axisColor, fontSize: 12 }}
          tickLine={false}
          axisLine={false}
          tickFormatter={yFormatter}
          width={50}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: tooltipBg,
            border: `1px solid ${tooltipBorder}`,
            borderRadius: 8,
            fontSize: 12,
          }}
          labelStyle={{ color: axisColor }}
          formatter={
            tooltipFormatter
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ? (value: any) => [tooltipFormatter(Number(value))]
              : undefined
          }
          labelFormatter={formatDateFull}
        />
        {series.length > 1 && (
          <Legend
            wrapperStyle={{ fontSize: 12 }}
            iconType="square"
            iconSize={10}
          />
        )}
        {series.map((s) =>
          type === 'line' ? (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              stroke={s.color}
              strokeWidth={2}
              dot={false}
              name={s.name}
            />
          ) : (
            <Bar
              key={s.key}
              dataKey={s.key}
              fill={s.color}
              name={s.name}
              stackId={s.stackId}
              radius={s.stackId ? undefined : [2, 2, 0, 0]}
            />
          )
        )}
      </Chart>
    </ResponsiveContainer>
  );
}
